import { useEffect, useRef } from 'react';

type UseDocumentVisibilityOptions = {
  /**
   * Also call the onChange with current document visibility.
   * @default false
   */
  callOnInit?: boolean;
};

/**
 * Hook to react to document visibility.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilityState
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event
 */
export function useDocumentVisibility(
  onChange: (isVisible: boolean) => void,
  { callOnInit = false }: UseDocumentVisibilityOptions = {},
): void {
  const hasCalledOnInitRef = useRef(false);

  useEffect(() => {
    const handleChange = (): void => {
      onChange(document.visibilityState === 'visible');
    };

    if (callOnInit && !hasCalledOnInitRef.current) {
      hasCalledOnInitRef.current = true;
      handleChange();
    }

    document.addEventListener('visibilitychange', handleChange);

    return () => {
      document.removeEventListener('visibilitychange', handleChange);
    };
  }, [onChange, callOnInit]);
}
